import { FC, useRef } from 'react'
import styles from './AutopilotFeatures.module.scss'
import { AutopilotFeaturesProps } from './types'
import { Button } from 'components/Phantom/Button'
import { Icon } from 'components/Phantom/Icon'
import { getDefaultFeatures } from './utils'
import { useGSAP } from '@gsap/react'
import SplitText from 'gsap/dist/SplitText'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { VideoGif } from 'components/basic/VideoGif'
import { useRefreshScrollTrigger } from 'components/_hooks/useRefreshScrollTrigger'
import { Type } from 'components/Type'

export const AutopilotFeatures: FC<AutopilotFeaturesProps> = (props) => {
	const { features = getDefaultFeatures() } = props
	const containerRef = useRef(null)

	useGSAP(() => {
		gsap.registerPlugin(ScrollTrigger)
		gsap.registerPlugin(SplitText)

		if (window.Localize?.getLanguage() === 'en' || !window.Localize) {
			const splitTimeline = gsap.timeline()

			const splitText = new SplitText('#autopilot-description', { type: 'words', tag: 'span' })
			splitTimeline.from(splitText.words, { duration: 2, opacity: 0.4, stagger: 0.2 })

			ScrollTrigger.create({
				trigger: '#autopilot-description',
				start: '-100 50%',
				end: '+=400',
				scrub: 1,
				animation: splitTimeline,
			})
		}

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: '#autopilot-feature-container',
				start: 'top 60%',
				end: '+=400',
				scrub: 1,
			},
		})

		tl.from('#autopilot-features-hero-superheader', { y: 150, opacity: 0 }, 0)
		tl.from('#autopilot-features-hero-header', { y: 300, opacity: 0 }, 0)
		tl.from('#autopilot-features-hero-video', { y: 450, opacity: 0 }, 0)
	})

	useRefreshScrollTrigger(containerRef)

	return (
		<div
			className={styles.container}
			ref={containerRef}
			id={'autopilot-feature-container'}
		>
			<header className={styles.header}>
				<Type.Headline3 as={'p'}>Powered by</Type.Headline3>
				<h2 id={'autopilot-features-hero-header'}>Autopilot</h2>

				<VideoGif
					src={'https://res.cloudinary.com/eightsleep/video/upload/f_auto,q_auto/2024-phantom-launch-pod4-autopilot-3-0.mp4'}
					sources={[
						{ src: 'https://d115sb6i1ixllw.cloudfront.net/assets/2024-phantom-launch/pod4-autopilot-3-0.mov', type: 'video/mp4; codecs="hvc1"' },
						{ src: 'https://d115sb6i1ixllw.cloudfront.net/assets/2024-phantom-launch/pod4-autopilot-3-0.webm', type: 'video/webm' },
						{ src: 'https://res.cloudinary.com/eightsleep/video/upload/f_auto,q_auto/2024-phantom-launch-pod4-autopilot-3-0.mp4', type: 'video/mpeg' },
					]}
					className={styles.hero_image}
					id={'autopilot-features-hero-video'}
				/>
			</header>

			<Type.Headline2
				className={styles.description}
				id={'autopilot-description'}
			>
				<span>Autopilot is the intelligence behind the Pod.</span>
				<span>It’s an algorithm that improves your sleep by analyzing your health and sleep metrics in real time.</span>
				<span>With the knowledge of over a million nights of data, Autopilot learns what you need and tailors your temperature and elevation automatically, without you having to lift a finger.</span>
			</Type.Headline2>

			<div className={styles.cta_container}>
				<Button.Empty
					id={'autopilot-cta'}
					href={'/autopilot'}
					className={styles.purple_button}
				>
					<Sparkle />
					Explore Autopilot
				</Button.Empty>
			</div>

			<article className={styles.features}>
				<header className={styles.features_header}>
					{/* <p>Factors</p> */}
					<Type.Headline3>Autopilot creates an individual blueprint to craft the environment for your best sleep</Type.Headline3>
				</header>

				<ul className={styles.features_list}>
					{features.map((feature) => (
						<li key={feature.text}>
							<Icon
								size={'48px'}
								name={feature.icon}
								color={'white'}
							/>
							<Type.Body1>{feature.text}</Type.Body1>
						</li>
					))}
				</ul>
			</article>
		</div>
	)
}

const Sparkle = () => {
	return (
		<svg
			width="23"
			height="23"
			viewBox="0 0 24 25"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M9.75312 19.3266L7.31562 13.9641L1.95312 11.5266L7.31562 9.08906L9.75312 3.72656L12.1906 9.08906L17.5531 11.5266L12.1906 13.9641L9.75312 19.3266ZM17.5531 21.2766L16.3344 18.5953L13.6531 17.3766L16.3344 16.1578L17.5531 13.4766L18.7719 16.1578L21.4531 17.3766L18.7719 18.5953L17.5531 21.2766Z"
				fill="white"
			/>
		</svg>
	)
}
